/**
 * API Helper Utility
 * Provides a wrapper around fetch that automatically handles authentication errors
 */

import { handleAuthError } from "./authUtils";

/**
 * Wrapper around fetch that automatically handles auth errors
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options (headers, method, body, etc.)
 * @param {Function} dispatch - Redux dispatch function (optional)
 * @returns {Promise<Response>} - Fetch response
 */
export const apiFetch = async (url, options = {}, dispatch = null) => {
  try {
    const response = await fetch(url, options);
    
    // For non-OK responses, try to parse the error
    if (!response.ok) {
      let result = null;
      try {
        result = await response.clone().json();
      } catch (e) {
        // If JSON parsing fails, create a basic error object
        result = {
          statusCode: response.status,
          message: response.statusText || "An error occurred",
        };
      }
      
      // Check for auth errors and handle them
      if (handleAuthError(result, response.status, dispatch)) {
        // Return a rejected promise so calling code can handle it if needed
        return Promise.reject(new Error("Authentication error"));
      }
    }
    
    return response;
  } catch (error) {
    // Re-throw network errors
    throw error;
  }
};

/**
 * Helper to get auth headers with token
 * @returns {Object} - Headers object with Authorization
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

